// 저장 전 투입자재 스캔 누락 행(SN/LOT + 수량>0 + no_barcode 없음)을 찾는다.
// 반환: 누락 row 1건 또는 undefined
// Scope: WRK_REG_POPUP menu-common only.
// Rule:
// - Save flow helpers shared by WP1001/WP3001 stay here.
// - Move to shared layer later only when reused across other menus.

// Purpose: find the first required material row missing a scanned barcode.
export const findMissingMaterialBarcodeRow = (grid2Rows: any[]) => {
  return (grid2Rows || []).find((row: any) => {
    const fgUnit = (row?.fg_unit || "").toString().trim().toUpperCase();
    if (fgUnit !== "SN" && fgUnit !== "LOT") return false;
    const qty = Number(row?.qt ?? row?.qt_w ?? row?.qt_unit ?? 0) || 0;
    if (qty <= 0) return false;
    return !(row?.no_barcode || "").toString().trim();
  });
};

// 저장 전 자재행 기본 검증 결과를 공통 판정한다.
// - hasMatlCheck=false 이면 검증 통과
// - grid2Rows가 비어 있으면 no_rows
// - 누락 바코드 행이 있으면 missing_barcode
// Purpose: shared pre-save validation result for material scan rows.
export const validateMaterialRowsBeforeSaveCommon = (
  hasMatlCheck: boolean,
  grid2Rows: any[]
):
  | { ok: true }
  | { ok: false; reason: "no_rows" }
  | { ok: false; reason: "missing_barcode"; row: any } => {
  if (!hasMatlCheck) return { ok: true };
  if (!grid2Rows.length) return { ok: false, reason: "no_rows" };

  const missingRow = findMissingMaterialBarcodeRow(grid2Rows);
  if (missingRow) {
    return { ok: false, reason: "missing_barcode", row: missingRow };
  }

  return { ok: true };
};

// grid2 스캔행을 prd_consl.insert payload 형식으로 변환한다.
// 입력 row 순서를 유지해 no_line을 1부터 부여한다.
// Purpose: convert scanned material rows into prd_consl insert payload rows.
export const buildManualConslInsertRows = (
  cdFirm: string | undefined,
  grid2Rows: any[],
  noRun: string
) => {
  return (grid2Rows || []).map((row: any, idx: number) => {
    const fgUnit = (row.fg_unit || "").toString().toUpperCase();
    const noBarcode = (row.no_barcode || "").toString();
    const noSn = row.no_sn || (fgUnit === "SN" ? noBarcode : undefined) || "";
    const noLot =
      row.no_lot || (fgUnit === "LOT" ? noBarcode : undefined) || "";
    const qt = Number(row.qt ?? row.qt_unit ?? 0) || 0;
    const qtW = Number(row.qt_w ?? qt) || 0;
    const qtB = Number(row.qt_b ?? 0) || 0;

    return {
      cd_firm: cdFirm,
      no_run: noRun,
      no_line: idx + 1,
      cd_matl: row.cd_matl,
      no_line_bill: row.no_line_bill,
      no_line_run: Number(row.no_line_run ?? 0) || 0,
      fg_unit: row.fg_unit,
      no_lot: noLot,
      no_sn: noSn,
      qt: qt,
      qt_w: qtW,
      qt_b: qtB,
      no_io: row.no_io,
      no_line_io: row.no_line_io,
      no_line_lot_io: row.no_line_lot_io,
      no_origin: row.no_origin,
      no_line_origin: row.no_line_origin,
      no_line_lot_origin: row.no_line_lot_origin,
      no_ct: row.no_ct,
      no_plt: row.no_plt,
      cd_wh: row.cd_wh,
      no_barcode: noBarcode,
    };
  });
};

// Y(스캔필수) 자재의 소요량 부족 여부를 계산한다.
// 부족 시 { matlCode, shortage }를 반환하고, 충족 시 null을 반환한다.
// Purpose: compute shortage for Y(required-scan) material lines.
export const findYMaterialShortage = (matlCheckRows: any[], conslRows: any[]) => {
  const yMatls = (matlCheckRows || []).filter(
    (row: any) => (row?.yn_matl_check || "").toString().trim().toUpperCase() === "Y"
  );
  if (yMatls.length < 1) return null;

  for (const matl of yMatls) {
    const qtNeed = Number(matl.qt_unit ?? 0) || 0;
    if (qtNeed <= 0) continue;
    const used = (conslRows || []).reduce((sum: number, row: any) => {
      if (row.cd_matl === matl.cd_matl && row.no_line_bill === matl.no_line_bill) {
        return sum + (Number(row.qt ?? 0) || 0);
      }
      return sum;
    }, 0);
    if (used < qtNeed) {
      return {
        matlCode: `${matl.cd_matl || ""}`,
        shortage: qtNeed - used,
      };
    }
  }

  return null;
};

// prd_consl -> prd_issuel 연결키(no_io/no_origin) 누락 행을 찾는다.
// 반환: 누락 row 1건 또는 undefined
// Purpose: detect consl rows missing issue-link keys before save.
export const findInvalidConslIssueKeyRow = (conslRows: any[]) => {
  return (conslRows || []).find((row: any) => {
    const qty = Number(row?.qt ?? 0) || 0;
    if (qty <= 0) return false;
    return !row?.no_io && !row?.no_origin;
  });
};

// prd_consh.insert 단건 payload를 생성한다.
// ynBf 값으로 fg_cons(BF/MAN)를 결정한다.
// Purpose: build one prd_consh insert row from run/save context.
export const buildConshInsertRows = (
  cdFirm: string | undefined,
  noCons: string,
  noRun: string,
  noWo: string | undefined,
  noLineWo: number | undefined,
  cdOp: string | undefined,
  ynBf: string | undefined,
  dtCons: string | undefined
) => {
  const fgCons = (ynBf || "N").toString().toUpperCase() === "Y" ? "BF" : "MAN";
  return [
    {
      cd_firm: cdFirm,
      no_cons: noCons,
      no_run: noRun,
      no_wo: noWo,
      no_line_wo: noLineWo,
      cd_op: cdOp,
      fg_cons: fgCons,
      dt_cons: dtCons,
    },
  ];
};
